/**
 * GTM(Google Tag Manager) 연동
 *
 * 배경 — CRM 팀 요청으로 그룹 공용 컨테이너(GTM-NTC6M9JB)를 붙인다.
 * 컨테이너 로드는 public/index.html 의 스니펫이 하고, 이 파일은 스니펫만으로는
 * 잡히지 않는 두 가지를 dataLayer 로 밀어 넣는다.
 *
 *   1) SPA 페이지뷰 — createWebHistory 라우터라 화면을 옮겨도 문서가 다시 로드되지 않는다.
 *      GTM 기본 페이지뷰는 최초 1회뿐이라 그대로 두면 전 화면이 "/" 하나로 집계된다.
 *   2) 앱/웹 구분 — 같은 컨테이너를 그룹 홈페이지들과 공유하므로, CRM 쪽에서
 *      앱 웹뷰 트래픽을 분리할 수 있어야 한다.
 *
 * 이 파일은 다른 모듈에 의존하지 않는다. GTM 은 기능 브랜치와 무관하게 붙고 떼야 하므로
 * 브랜치마다 존재 여부가 갈리는 파일(utils/appVersion.js 등)을 import 하지 않는다.
 */

/** CRM 팀 공용 컨테이너 ID. 그룹 홈페이지(parnashotel.com 등)와 같은 값이다. */
export const GTM_ID = "GTM-NTC6M9JB"

/**
 * page_location 에서 지워야 하는 쿼리 파라미터.
 *
 * 결제 복귀 화면이 payment-id, result-message 를 쿼리로 달고 들어온다
 * (서버 PaymentService.buildReturnHtml). 그대로 두면 결제 식별자가 GA 에 그대로 쌓인다.
 */
const STRIP_PARAMS = ["payment-id", "result-message", "token", "accessToken", "refreshToken"]

function push(payload) {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(payload)
}

/**
 * 앱 웹뷰인지 판별한다.
 *
 * UA 만으로는 앱과 모바일 브라우저가 구분되지 않는다(커스텀 UA 마커 이전 버전 때문).
 * 네이티브가 주입하는 브릿지의 존재 여부로 판별한다.
 * 서버 AppClientUtil / utils/appVersion.js 와 같은 판정 규칙이다.
 *
 * @returns {{isApp: Boolean, platform: String}} platform 은 "AOS" | "IOS" | "WEB"
 */
export function getClientInfo() {
    const ua = navigator.userAgent

    const isApp = !!(
        (window.ParnasApp && window.ParnasApp.login) ||
        (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.login)
    )

    const platform = /iPhone|iPad|iPod/i.test(ua) ? "IOS"
        : /Android/i.test(ua) ? "AOS"
            : "WEB"

    return { isApp, platform }
}

/** 결제 식별자 등을 걷어낸 절대 URL */
function cleanLocation(fullPath) {
    const url = new URL(fullPath, window.location.origin)
    STRIP_PARAMS.forEach((k) => url.searchParams.delete(k))
    return url.toString()
}

/**
 * 컨테이너가 첫 태그를 쏘기 전에 클라이언트 정보를 올려둔다.
 * app.mount() 보다 먼저 호출해야 최초 페이지뷰에도 값이 실린다.
 */
export function initGtm() {
    const { isApp, platform } = getClientInfo()
    push({ is_app: isApp, client_platform: platform })
}

/**
 * 라우트 이동을 가상 페이지뷰로 올린다.
 *
 * 최초 진입은 올리지 않는다. 문서가 실제로 로드되는 시점이라 컨테이너의 Google 태그가
 * 이미 자동으로 페이지뷰를 보내며, 여기서 또 올리면 첫 화면만 두 번 집계된다.
 *
 * 이벤트명을 page_view 가 아니라 virtual_page_view 로 둔 것도 같은 이유다.
 * GTM 콘솔에서 자동 페이지뷰와 구분되어야 트리거를 따로 걸 수 있다.
 */
let firstNavigation = true

export function sendPageView(to) {
    if (firstNavigation) {
        firstNavigation = false
        return
    }

    push({
        event: "virtual_page_view",
        page_path: to.path,
        page_location: cleanLocation(to.fullPath),
        page_title: document.title,
    })
}
